import type { DtoV1IncomeCalculatorYmlCalculationData } from '~/__generated__/api';
import Alert from '~/common/components/Alerts/Alert';
import Box from '~/common/components/Box';
import Flex from '~/common/components/Flex';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import { formatCurrency, reverseFormatCurrency } from '~/common/util/format';
import BreakdownContainer from '~/pages/IncomeCalculator/Pages/ReviewBreakdown/BreakdownContainer';
import {
  type AdditionalCalculationDetails,
  RecentRaiseSection,
} from '~/pages/IncomeCalculator/Pages/ReviewBreakdown/RecentRaise';

import QualifyingCalculationDataGrid from './QualifyingCalculationDataGrid';

type QualifyingCalculationBreakdownProps = {
  selectedResult: DtoV1IncomeCalculatorYmlCalculationData;
  hideAccordion?: boolean;
  incluldeAlerts?: boolean;
  fullWidth?: boolean;
};

export default function QualifyingCalculationBreakdown({
  selectedResult,
  hideAccordion = false,
  incluldeAlerts = false,
  fullWidth = false,
}: Readonly<QualifyingCalculationBreakdownProps>) {
  return (
    <Box>
      {selectedResult.calculations.map((calculation) => {
        const isRecentRaise = !!calculation?.additionalCalculationDetails;
        const title = `${calculation.title}${
          (calculation.trend && `  ${calculation.trend}`) || ''
        }`;
        const incomeTypeTitle = isRecentRaise
          ? `${title} - Recent Raise`
          : title;
        return (
          <Box mb={4} key={calculation.incomeType}>
            <BreakdownContainer
              header={incomeTypeTitle}
              calculatedAmount={reverseFormatCurrency(calculation.value)}
              hideAccordion={hideAccordion}
            >
              {hideAccordion && (
                <Flex
                  justifyContent="space-between"
                  sx={{ textDecoration: 'underline' }}
                  fontWeight="bold"
                  mb="1rem"
                >
                  <Text>{title}</Text>
                  <Text>{formatCurrency(calculation.value)}</Text>
                </Flex>
              )}
              <Stack>
                {!isRecentRaise &&
                  calculation.cardData.map((field) => {
                    const {
                      isOneW2Grid = false,
                      isPaystubGrid = false,
                      isTwoW2Grid = false,
                      isVoeGrid = false,
                    } = field.styling || {};
                    const shouldUseGrid =
                      isOneW2Grid || isPaystubGrid || isTwoW2Grid || isVoeGrid;

                    if (shouldUseGrid)
                      return (
                        <Box key={field.title} m={4}>
                          <QualifyingCalculationDataGrid
                            gridType={field.styling}
                            gridData={field.grid}
                          />
                        </Box>
                      );

                    return (
                      <Text key={field.title}>
                        <strong>{field.title}</strong> {field.description}
                      </Text>
                    );
                  })}
                {isRecentRaise && (
                  <RecentRaiseSection
                    additionalCalculationDetails={
                      calculation.additionalCalculationDetails as AdditionalCalculationDetails
                    }
                    fullWidth={fullWidth}
                  />
                )}
                {incluldeAlerts && calculation?.alert?.showAlert && (
                  <Alert
                    title={calculation.alert.title}
                    status={calculation.alert.severity}
                  >
                    {calculation.alert.description}
                  </Alert>
                )}
              </Stack>
            </BreakdownContainer>
          </Box>
        );
      })}
    </Box>
  );
}
